import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, LogIn, LogOut as LogOutIcon, Calendar, Clock, Users, KeyRound, User, Check, X, CircleUser } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TherapistAttendance = ({ user, onLogout }) => {
  const [propertyTherapists, setPropertyTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Identity verification state
  const [step, setStep] = useState('select'); // 'select' | 'pin' | 'attendance'
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState('');
  
  // Attendance state for verified therapist
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);

  useEffect(() => {
    fetchTherapists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTherapists = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API}/therapists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const filteredTherapists = response.data.filter(
        t => t.assigned_property_id === user.assigned_property_id && t.status !== 'archived'
      );
      setPropertyTherapists(filteredTherapists);
    } catch (error) {
      console.error('Failed to load therapists:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTherapistSelect = (therapistId) => {
    const therapist = propertyTherapists.find(t => t.user_id === therapistId);
    setSelectedTherapist(therapist);
    setStep('pin');
    setPinValue('');
    setPinError('');
  };

  const handlePinVerify = async () => {
    if (pinValue.length !== 4) {
      setPinError('Please enter a 4-digit PIN');
      return;
    }
    
    setActionLoading(true);
    setPinError('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Verify PIN
      await axios.post(
        `${API}/attendance/verify-pin`,
        {
          therapist_id: selectedTherapist.user_id,
          pin: pinValue
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // PIN verified - fetch attendance for this therapist
      await fetchTherapistAttendance();
      setStep('attendance');
      toast.success(`Welcome, ${selectedTherapist.full_name?.split(' ')[0]}!`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'PIN verification failed';
      if (errorMsg.includes('Invalid PIN')) {
        setPinError('Invalid PIN. Please try again.');
      } else {
        setPinError(errorMsg);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const fetchTherapistAttendance = async () => {
    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch attendance for the selected therapist
      const response = await axios.get(
        `${API}/attendance/property/${encodeURIComponent(user.assigned_property_id)}/therapists`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Handle both array and object response structures
      const therapistsList = Array.isArray(response.data) ? response.data : (response.data.therapists || []);
      const therapistData = therapistsList.find(t => 
        t.user_id === selectedTherapist.user_id || t.therapist_id === selectedTherapist.user_id
      );
      
      if (therapistData) {
        setTodayAttendance({
          checked_in: !!therapistData.check_in_time,
          checked_out: !!therapistData.check_out_time,
          check_in_time: therapistData.check_in_time,
          check_out_time: therapistData.check_out_time
        });
      } else {
        setTodayAttendance({
          checked_in: false,
          checked_out: false
        });
      }
      
      // Fetch attendance history
      try {
        const historyResponse = await axios.get(
          `${API}/attendance/admin/history/${selectedTherapist.user_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAttendanceHistory(historyResponse.data.attendance_records || []);
      } catch (err) {
        setAttendanceHistory([]);
      }
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
      // Set default state so the UI still works
      setTodayAttendance({
        checked_in: false,
        checked_out: false
      });
    }
  };

  const handleCheckIn = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API}/attendance/pin-check-in`,
        {
          therapist_id: selectedTherapist.user_id,
          pin: pinValue,
          gps_location: null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Checked in successfully!');
      await fetchTherapistAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API}/attendance/pin-check-out`,
        {
          therapist_id: selectedTherapist.user_id,
          pin: pinValue
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Checked out successfully!');
      await fetchTherapistAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-out failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBackToSelect = () => {
    setStep('select');
    setSelectedTherapist(null);
    setPinValue('');
    setPinError('');
    setTodayAttendance(null);
    setAttendanceHistory([]);
  };

  const formatTime = (isoString) => {
    if (!isoString) return '--:--';
    return new Date(isoString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-border/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {step === 'attendance' ? (
              <Button variant="outline" size="sm" onClick={handleBackToSelect} data-testid="back-to-select">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            ) : (
              <Link to="/therapist">
                <Button variant="outline" size="sm" data-testid="back-button">
                  <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </Link>
            )}
            <h1 className="text-2xl font-serif text-foreground">Attendance</h1>
          </div>
          <Button variant="outline" size="sm" onClick={onLogout}>
            <LogOutIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8">
        {/* Step 1: Select Who You Are */}
        {step === 'select' && (
          <div className="glass rounded-2xl p-6 space-y-6" data-testid="select-therapist-step">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-serif text-foreground mb-2">Who are you?</h2>
              <p className="text-muted-foreground">
                Select your name and enter your PIN to sign in
              </p>
              <Badge variant="outline" className="mt-2">
                {user.assigned_property_id}
              </Badge>
            </div>
            
            <RadioGroup
              value={selectedTherapist?.user_id || ''}
              onValueChange={handleTherapistSelect}
              className="space-y-3"
            >
              {propertyTherapists.map(therapist => (
                <div
                  key={therapist.user_id}
                  className="flex items-center space-x-3 p-4 rounded-xl border-2 border-transparent hover:border-primary/30 bg-muted/30 cursor-pointer transition-all"
                  onClick={() => handleTherapistSelect(therapist.user_id)}
                  data-testid={`therapist-option-${therapist.user_id}`}
                >
                  <RadioGroupItem value={therapist.user_id} id={therapist.user_id} />
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <CircleUser className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <Label htmlFor={therapist.user_id} className="font-medium text-foreground cursor-pointer">
                        {therapist.full_name}
                      </Label>
                      <p className="text-xs text-muted-foreground">{therapist.email}</p>
                    </div>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>
        )}

        {/* Step 2: Enter PIN */}
        {step === 'pin' && selectedTherapist && (
          <div className="glass rounded-2xl p-6 space-y-6" data-testid="pin-step">
            <Button variant="ghost" size="sm" onClick={handleBackToSelect} className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-serif text-foreground mb-2">Enter Your PIN</h2>
              <p className="text-lg font-medium text-primary">{selectedTherapist.full_name}</p>
            </div>
            
            <div className="space-y-4">
              <div className="text-center">
                <Label className="text-sm text-muted-foreground">4-digit PIN</Label>
                <Input
                  type="password"
                  maxLength={4}
                  value={pinValue}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setPinValue(val);
                    setPinError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && pinValue.length === 4) {
                      handlePinVerify();
                    }
                  }}
                  className="mt-3 text-center text-3xl tracking-[0.5em] font-mono h-16 max-w-[200px] mx-auto"
                  placeholder="••••"
                  autoFocus
                  data-testid="pin-input"
                />
                {pinError && (
                  <p className="text-sm text-red-500 mt-3 flex items-center justify-center gap-1">
                    <X className="w-4 h-4" /> {pinError}
                  </p>
                )}
              </div>
              
              <Button
                className="w-full h-12"
                onClick={handlePinVerify}
                disabled={pinValue.length !== 4 || actionLoading}
                data-testid="verify-pin-btn"
              >
                {actionLoading ? 'Verifying...' : 'Verify & Continue'}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Attendance Actions */}
        {step === 'attendance' && selectedTherapist && todayAttendance && (
          <div className="space-y-6" data-testid="attendance-step">
            {/* Verified User Info */}
            <div className="glass rounded-2xl p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-serif text-foreground mb-1">{selectedTherapist.full_name}</h2>
              <p className="text-muted-foreground">{user.assigned_property_id}</p>
            </div>

            {/* Today's Attendance */}
            <div className="glass rounded-2xl p-6" data-testid="today-attendance">
              <h3 className="text-lg font-serif text-foreground mb-4">Today's Attendance</h3>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className={`rounded-xl p-4 ${todayAttendance.checked_in ? 'bg-green-50 border border-green-200' : 'bg-muted/30'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <LogIn className={`w-5 h-5 ${todayAttendance.checked_in ? 'text-green-600' : 'text-muted-foreground'}`} strokeWidth={1.5} />
                    <p className="text-sm text-muted-foreground">Check-in</p>
                  </div>
                  <p className={`text-2xl font-medium ${todayAttendance.checked_in ? 'text-green-600' : 'text-muted-foreground'}`}>
                    {formatTime(todayAttendance.check_in_time)}
                  </p>
                </div>

                <div className={`rounded-xl p-4 ${todayAttendance.checked_out ? 'bg-blue-50 border border-blue-200' : 'bg-muted/30'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <LogOutIcon className={`w-5 h-5 ${todayAttendance.checked_out ? 'text-blue-600' : 'text-muted-foreground'}`} strokeWidth={1.5} />
                    <p className="text-sm text-muted-foreground">Check-out</p>
                  </div>
                  <p className={`text-2xl font-medium ${todayAttendance.checked_out ? 'text-blue-600' : 'text-muted-foreground'}`}>
                    {formatTime(todayAttendance.check_out_time)}
                  </p>
                </div>
              </div>

              {/* Action Button */}
              {!todayAttendance.checked_in ? (
                <Button
                  className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
                  onClick={handleCheckIn}
                  disabled={actionLoading}
                  data-testid="checkin-btn"
                >
                  <LogIn className="w-5 h-5 mr-2" />
                  {actionLoading ? 'Processing...' : 'Check In'}
                </Button>
              ) : !todayAttendance.checked_out ? (
                <Button
                  className="w-full h-14 text-lg bg-red-600 hover:bg-red-700"
                  onClick={handleCheckOut}
                  disabled={actionLoading}
                  data-testid="checkout-btn"
                >
                  <LogOutIcon className="w-5 h-5 mr-2" />
                  {actionLoading ? 'Processing...' : 'Check Out'}
                </Button>
              ) : (
                <div className="text-center p-4 bg-green-50 rounded-xl border border-green-200">
                  <Check className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <p className="text-green-700 font-medium">Attendance Complete for Today</p>
                </div>
              )}
            </div>

            {/* Attendance History */}
            <div className="glass rounded-2xl p-6">
              <h3 className="text-lg font-serif text-foreground mb-4">Recent History</h3>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {attendanceHistory.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No attendance records yet</p>
                ) : (
                  attendanceHistory.slice(0, 10).map((record, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{record.date}</p>
                          <p className="text-xs text-muted-foreground">
                            In: {formatTime(record.check_in_time)} • Out: {formatTime(record.check_out_time)}
                          </p>
                        </div>
                      </div>
                      {record.check_out_time ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">Complete</Badge>
                      ) : record.check_in_time ? (
                        <Badge className="bg-yellow-100 text-yellow-700 text-xs">Active</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700 text-xs">Absent</Badge>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TherapistAttendance;
